'use client';

/**
 * Controller hook for the AppShell split. Owns every store/router/
 * query read the layout needs, the four effects that keep the shell
 * in sync (nowTick, lobby-banner-clear-in-room, socket connect/
 * disconnect, ranked-geo-hint storage sync), every derived flag for
 * banner visibility, and the eight UI callbacks the shell wires to
 * its buttons.
 *
 * The shell + scene components are pure renderers over the object
 * this hook returns.
 */

import { useRealtimePrincipal } from '@/lib/realtime/realtime-principal';
import { useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/shallow';
import { usePathname, useRouter } from 'next/navigation';
import { useLocale } from '@/contexts/LocaleContext';
import { usePlayer } from '@/contexts/PlayerContext';
import { useAuthStore } from '@/stores/auth.store';
import { useRealtimeMatchStore } from '@/stores/realtimeMatch.store';
import { useRankedMatchmakingStore } from '@/stores/rankedMatchmaking.store';
import { useAuctionActiveMatchStore } from '@/stores/auctionActiveMatch.store';
import { useGameSessionStore } from '@/stores/gameSession.store';
import { useStoreWallet } from '@/lib/queries/store.queries';
import { useIncomingFriendRequestCount } from '@/lib/queries/social.queries';
import { useUnreadNotificationCount } from '@/lib/queries/notifications.queries';
import { getSocket } from '@/lib/realtime/socket-client';
import { logger } from '@/utils/logger';
import { useLobbyCommandMachine } from '@/features/friend/hooks/useLobbyCommandMachine';

import { readCachedRankedGeoHint, type RankedGeoHint } from '@/lib/match/rankedGeoHint';
import {
  HIDE_NAV_PATHS,
  isPathActive as isPathActiveHelper,
  navPathOf,
  showsHeader,
} from './appShell.helpers';

export function useAppShellViewModel() {
  const pathname = usePathname();
  const router = useRouter();
  const { t, locale } = useLocale();
  const { player: playerStats } = usePlayer();
  const { data: storeWallet } = useStoreWallet();
  const { data: incomingFriendRequestCount = 0 } = useIncomingFriendRequestCount();
  const unreadNotificationCount = useUnreadNotificationCount();
  const authUser = useAuthStore((state) => state.user);
  const authStatus = useAuthStore((state) => state.status);
  const logout = useAuthStore((state) => state.logout);
  const lobby = useRealtimeMatchStore((state) => state.lobby);
  const draft = useRealtimeMatchStore((state) => state.draft);
  const matchBanner = useRealtimeMatchStore(useShallow((s) => ({
    matchId: s.match?.matchId ?? null,
    mode: s.match?.mode ?? null,
    variant: s.match?.variant ?? null,
    opponent: s.match?.opponent ?? null,
    finalResults: s.match?.finalResults ?? null,
  })));
  const remainingReconnects = useRealtimeMatchStore((state) => state.remainingReconnects);
  const sessionState = useRealtimeMatchStore((state) => state.sessionState);
  const rejoinMatch = useRealtimeMatchStore((state) => state.rejoinMatch);
  const forfeitPending = useRealtimeMatchStore((state) => state.forfeitPending);
  const partyDropout = useRealtimeMatchStore((state) => state.partyDropout);
  const setForfeitPending = useRealtimeMatchStore((state) => state.setForfeitPending);
  const clearPartyDropout = useRealtimeMatchStore((state) => state.clearPartyDropout);
  const challengeInviteCount = useRealtimeMatchStore((state) => state.challengeInvites.length);
  const suppressLobbyBannerUntil = useRealtimeMatchStore((state) => state.suppressLobbyBannerUntil);
  const suppressLobbyBannerReason = useRealtimeMatchStore((state) => state.suppressLobbyBannerReason);
  const clearLobbyBannerSuppression = useRealtimeMatchStore((state) => state.clearLobbyBannerSuppression);
  const clearRejoinAvailable = useRealtimeMatchStore((state) => state.clearRejoinAvailable);
  const autoRejoinSuppressedMatchId = useRealtimeMatchStore((state) => state.autoRejoinSuppressedMatchId);
  const resetRealtime = useRealtimeMatchStore((state) => state.reset);
  const activeAuctionMatch = useAuctionActiveMatchStore((state) => state.activeAuctionMatch);
  const startSession = useGameSessionStore((state) => state.startSession);
  const setGameStage = useGameSessionStore((state) => state.setStage);
  const [socketConnected, setSocketConnected] = useState(false);
  // Read after mount (see the sync effect): the cached hint lives in browser storage, which the server render cannot see.
  const [rankedGeoHintDebug, setRankedGeoHintDebug] = useState<RankedGeoHint | null>(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const lobbyCommands = useLobbyCommandMachine();
  // One connection owner for members AND resolved guests (friend rooms).
  const principal = useRealtimePrincipal();

  const isAuthenticated = authStatus === 'authenticated';
  useEffect(() => {
    if (suppressLobbyBannerUntil === null) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [suppressLobbyBannerUntil]);

  const currentPath = pathname ?? '/';
  // A daily-challenge GAME (`/daily/challenges/<type>`) is an immersive fullscreen
  // experience like a ranked match — hide the app header AND bottom nav so they
  // can't be reached and content can't scroll behind them. The hub
  // (`/daily/challenges`) keeps both.
  const inDailyChallengeGame = currentPath.startsWith('/daily/challenges/');
  const showHeader = !inDailyChallengeGame && showsHeader(currentPath);
  const navPath = navPathOf(currentPath);
  const showNav = !inDailyChallengeGame && !HIDE_NAV_PATHS.some((path) => currentPath.startsWith(path));
  const inLobbyRoom = currentPath.startsWith('/friend/room');
  const lobbyBannerSuppressed =
    suppressLobbyBannerReason !== null ||
    (suppressLobbyBannerUntil !== null && suppressLobbyBannerUntil > nowTick);
  const lobbyIncludesCurrentUser =
    !!principal.userId &&
    !!lobby &&
    lobby.members.some((member) => member.userId === principal.userId);
  // Not on game screens: the game covers the banner there, but its button stayed reachable by keyboard.
  const showLobbyBanner =
    showNav &&
    !!lobby &&
    lobbyIncludesCurrentUser &&
    lobby.status === 'waiting' &&
    lobby.mode === 'friendly' &&
    Boolean(lobby.inviteCode) &&
    !inLobbyRoom &&
    !lobbyBannerSuppressed;
  const showRankedLobbyBanner =
    !!lobby &&
    lobbyIncludesCurrentUser &&
    lobby.status === 'waiting' &&
    lobby.mode === 'ranked' &&
    !currentPath.startsWith('/game');

  useEffect(() => {
    if (inLobbyRoom && suppressLobbyBannerReason !== null) {
      clearLobbyBannerSuppression();
    }
  }, [clearLobbyBannerSuppression, inLobbyRoom, suppressLobbyBannerReason]);

  const draftOpponent = lobby?.members.find((member) => member.userId !== authUser?.id);
  const activeDraftBanner = lobby?.status === 'active' && draft
    ? {
        lobbyId: lobby.lobbyId,
        mode: lobby.mode,
        opponent: draftOpponent,
      }
    : null;
  const showDraftBanner = !!activeDraftBanner && !inLobbyRoom && !currentPath.startsWith('/game');
  const activeMatchBanner = rejoinMatch
    ? {
        matchId: rejoinMatch.matchId,
        mode: rejoinMatch.mode,
        opponent: rejoinMatch.opponent,
        rejoinOfferCreatedAt: rejoinMatch.createdAt,
        source: 'rejoin' as const,
      }
    : matchBanner.matchId && !matchBanner.finalResults
      ? {
          matchId: matchBanner.matchId,
          mode: matchBanner.mode!,
          opponent: matchBanner.opponent!,
          rejoinOfferCreatedAt: null,
          source: 'active' as const,
        }
      : null;
  const forfeitPendingForActiveMatch =
    !!forfeitPending &&
    !!activeMatchBanner &&
    forfeitPending.matchId === activeMatchBanner.matchId;
  const partyDropoutForActiveMatch =
    !!partyDropout &&
    !!activeMatchBanner &&
    partyDropout.matchId === activeMatchBanner.matchId;
  const showRejoinBanner =
    !!activeMatchBanner &&
    !forfeitPendingForActiveMatch &&
    !partyDropoutForActiveMatch &&
    !inLobbyRoom &&
    !currentPath.startsWith('/game');
  // Auction "still in a live auction — rejoin" banner. Route-gated off /auction
  // (the auction flow screen owns the in-match handshake there) and /game.
  const showAuctionRejoinBanner =
    !!activeAuctionMatch &&
    !inLobbyRoom &&
    !currentPath.startsWith('/game') &&
    !currentPath.startsWith('/auction');
  const completedMatchBanner = matchBanner.finalResults
    ? {
        matchId: matchBanner.matchId!,
        mode: matchBanner.mode!,
        variant: matchBanner.variant!,
        opponent: matchBanner.opponent!,
      }
    : null;
  const showCompletedMatchBanner = !!completedMatchBanner && !currentPath.startsWith('/game');
  const showForfeitPendingBanner = !!forfeitPending && !matchBanner.finalResults && !currentPath.startsWith('/game');
  const showPartyDropoutBanner = !!partyDropout && !matchBanner.finalResults && !currentPath.startsWith('/game');
  const forfeitPendingTitle =
    forfeitPending?.reason === 'self_forfeit'
      ? t('forfeit.matchForfeited')
      : forfeitPending?.reason === 'opponent_forfeit'
        ? t('forfeit.opponentForfeited')
        : forfeitPending?.reason === 'opponent_reconnect_limit'
          ? t('forfeit.opponentDidNotReconnect')
          : t('forfeit.youLostMatch');
  const forfeitPendingDescription = forfeitPending?.message ?? t('forfeit.finalizingResult');
  const completedByForfeit = matchBanner.finalResults?.winnerDecisionMethod === 'forfeit';
  const completedPartyQuiz = completedMatchBanner?.variant === 'friendly_party_quiz';
  const rejoinReconnectsLeft = rejoinMatch?.remainingReconnects ?? remainingReconnects ?? 0;
  const lobbyCode = lobby?.inviteCode ?? '';
  const showLobbyDebug = process.env.NODE_ENV !== 'production';
  const localWaitingLobbyId = lobby?.status === 'waiting' ? lobby.lobbyId : null;
  const sessionWaitingLobbyId = sessionState?.waitingLobbyId ?? null;
  const lobbyDebugMismatch = localWaitingLobbyId !== sessionWaitingLobbyId;
  const sessionStateLabel = sessionState?.state ?? 'NO_SESSION';
  const navbarCoins = storeWallet?.coins ?? 0;
  const navbarTickets = storeWallet?.tickets ?? 0;
  // Two separate badges: the SOCIAL tab counts what that tab resolves (friend
  // requests + challenge invites); the BELL counts general notifications. The
  // old combined number double-badged every WL notification on both icons.
  const socialBadgeCount = incomingFriendRequestCount + challengeInviteCount;
  const bellBadgeCount = unreadNotificationCount;

  // The socket manager and the debug poll exist for members only: a guest on
  // the public hub must not construct a socket or run timers.
  useEffect(() => {
    if (!isAuthenticated) return;
    const socket = getSocket();
    setSocketConnected(socket.connected);
    const handleConnect = () => setSocketConnected(true);
    const handleDisconnect = () => setSocketConnected(false);
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const sync = () => setRankedGeoHintDebug(readCachedRankedGeoHint());
    // First read after mount (off the effect tick, per the cascading-renders lint rule).
    queueMicrotask(sync);
    window.addEventListener('storage', sync);
    const intervalId = window.setInterval(sync, 1500);
    return () => {
      window.removeEventListener('storage', sync);
      window.clearInterval(intervalId);
    };
  }, [isAuthenticated]);

  const handleLogout = async () => {
    await logout();
    // Land on the guest Play page — the landing no longer exists.
    router.replace('/play');
  };

  const isPathActive = (path: string, exact?: boolean) => isPathActiveHelper(navPath, path, exact);

  const handleReturnToLobby = () => {
    if (!lobbyCode) return;
    router.push(`/friend/room/${lobbyCode}?source=current_lobby`);
  };

  const handleReturnToRankedLobby = () => {
    startSession({
      mode: 'ranked',
      matchType: 'ranked',
      questionCount: 10,
      opponentId: draftOpponent?.userId ?? 'opponent',
      opponentUsername: draftOpponent?.username ?? 'Opponent',
      opponentAvatar: draftOpponent?.avatarUrl ?? undefined,
    });
    setGameStage('matchmaking');
    router.push('/game');
  };

  const handleLeaveLobby = () => {
    if (lobbyCommands.isLeaving) return;
    void lobbyCommands.leaveLobby()
      .then((result) => {
        if (!result?.ok) return;
        resetRealtime();
        useRankedMatchmakingStore.getState().clearRankedMatchmaking();
      })
      .catch((error) => {
        logger.warn('Failed to leave lobby', { error });
      });
  };

  const handleRejoinMatch = () => {
    if (!activeMatchBanner) return;

    const matchId = activeMatchBanner.matchId;

    startSession({
      mode: activeMatchBanner.mode === 'ranked' ? 'ranked' : 'quizball',
      matchType: activeMatchBanner.mode === 'ranked' ? 'ranked' : 'friendly',
      questionCount: 10,
      opponentId: activeMatchBanner.opponent.id,
      opponentUsername: activeMatchBanner.opponent.username,
      opponentAvatar: activeMatchBanner.opponent.avatarUrl ?? undefined,
    });
    // Avoid re-entering matchmaking queue on rejoin.
    setGameStage('playing');

    if (activeMatchBanner.source === 'rejoin') {
      getSocket().emit('match:rejoin', { matchId });
      clearRejoinAvailable();
    }

    router.push('/game');
  };

  // Auto-rejoin on reload: a tab refresh mid-match resets the client game
  // state, so the user used to land on /play and had to press "Rejoin"
  // manually (and only when the disconnect-grace pause won the reconnect
  // race — a fast reload resumed silently, making behavior feel random).
  // Make it uniform: as soon as the server confirms a live match for this
  // session (match:rejoin_available cross-checked against session:state
  // IN_ACTIVE_MATCH), perform exactly what the Rejoin button does.
  //
  // Guards:
  //  - source === 'rejoin' only (reload/disconnect path, never live navigation)
  //  - showRejoinBanner reuses all existing suppressions (forfeit pending,
  //    party dropout, /friend/room, already on /game)
  //  - autoRejoinSuppressedMatchId skips matches the user intentionally left
  //  - once per server offer (matchId + createdAt), so a later disconnect in
  //    the same match is retried without allowing one offer to loop
  const autoRejoinAttemptedOfferRef = useRef<string | null>(null);
  const canAutoRejoin =
    showRejoinBanner &&
    !!activeMatchBanner &&
    activeMatchBanner.source === 'rejoin' &&
    sessionState?.state === 'IN_ACTIVE_MATCH' &&
    sessionState.activeMatchId === activeMatchBanner.matchId &&
    autoRejoinSuppressedMatchId !== activeMatchBanner.matchId;
  const autoRejoinOffer = canAutoRejoin && activeMatchBanner
    ? {
        matchId: activeMatchBanner.matchId,
        key: `${activeMatchBanner.matchId}:${activeMatchBanner.rejoinOfferCreatedAt ?? 0}`,
      }
    : null;
  useEffect(() => {
    if (!autoRejoinOffer) return;
    if (autoRejoinAttemptedOfferRef.current === autoRejoinOffer.key) return;
    autoRejoinAttemptedOfferRef.current = autoRejoinOffer.key;
    logger.info('Auto-rejoining active match after reload/reconnect', { matchId: autoRejoinOffer.matchId });
    handleRejoinMatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleRejoinMatch is recreated every render; the offer key + ref guard make this fire at most once per offer
  }, [autoRejoinOffer?.key]);

  // Rejoin a live auction from the home/play banner. Navigating to /auction
  // remounts AuctionFlowScreen, which captures the match id from this store in
  // a mount-time lazy initializer — so the store must NOT be cleared here or
  // the screen reads null and starts a fresh search instead of re-attaching.
  // The banner is already route-gated off /auction; the store clears naturally
  // on the next auction:state / match_finished event.
  const handleRejoinAuction = () => {
    router.push('/auction');
  };

  const handleReturnToDraft = () => {
    if (!activeDraftBanner) return;

    startSession({
      mode: activeDraftBanner.mode === 'ranked' ? 'ranked' : 'quizball',
      matchType: activeDraftBanner.mode === 'ranked' ? 'ranked' : 'friendly',
      questionCount: 10,
      opponentId: activeDraftBanner.opponent?.userId ?? 'opponent',
      opponentUsername: activeDraftBanner.opponent?.username ?? 'Opponent',
      opponentAvatar: activeDraftBanner.opponent?.avatarUrl ?? undefined,
      skipDraftShowdown: true,
    });
    setGameStage('categoryBlocking');
    router.push('/game');
  };

  const handleForfeitRejoin = () => {
    if (!activeMatchBanner) {
      clearRejoinAvailable();
      return;
    }
    setForfeitPending({
      matchId: activeMatchBanner.matchId,
      reason: 'self_forfeit',
      message: t('forfeit.finalizingResult'),
    });
    getSocket().emit('match:forfeit', { matchId: activeMatchBanner.matchId });
  };

  const handleViewCompletedMatch = () => {
    if (!completedMatchBanner) return;

    startSession({
      mode: completedMatchBanner.mode === 'ranked' ? 'ranked' : 'quizball',
      matchType: completedMatchBanner.mode === 'ranked' ? 'ranked' : 'friendly',
      questionCount: 10,
      opponentId: completedMatchBanner.opponent.id,
      opponentUsername: completedMatchBanner.opponent.username,
      opponentAvatar: completedMatchBanner.opponent.avatarUrl ?? undefined,
    });
    setGameStage('finalResults');
    router.push('/game');
  };

  const handleDismissCompletedMatch = () => {
    resetRealtime();
    useRankedMatchmakingStore.getState().clearRankedMatchmaking();
  };

  const handleDismissPartyDropout = () => {
    clearPartyDropout();
  };

  return {
    // Locale / router
    t,
    locale,
    router,
    // Player / auth
    playerStats,
    authUser,
    // Route
    currentPath,
    navPath,
    showHeader,
    showNav,
    isPathActive,
    // Lobby / draft / match data
    lobby,
    lobbyCode,
    draftOpponent,
    activeDraftBanner,
    activeMatchBanner,
    activeAuctionMatch,
    completedMatchBanner,
    forfeitPending,
    partyDropout,
    forfeitPendingTitle,
    forfeitPendingDescription,
    completedByForfeit,
    completedPartyQuiz,
    rejoinReconnectsLeft,
    // Banner show flags
    showLobbyBanner,
    showRankedLobbyBanner,
    showDraftBanner,
    showRejoinBanner,
    showAuctionRejoinBanner,
    showCompletedMatchBanner,
    showForfeitPendingBanner,
    showPartyDropoutBanner,
    // Currency + badges
    navbarCoins,
    navbarTickets,
    socialBadgeCount,
    bellBadgeCount,
    // Realtime
    socketConnected,
    // Debug
    showLobbyDebug,
    lobbyDebugMismatch,
    localWaitingLobbyId,
    sessionWaitingLobbyId,
    sessionStateLabel,
    rankedGeoHintDebug,
    // Logout modal state
    showLogoutConfirm,
    setShowLogoutConfirm,
    // Callbacks
    handleLogout,
    handleReturnToLobby,
    handleReturnToRankedLobby,
    handleLeaveLobby,
    handleRejoinMatch,
    handleRejoinAuction,
    handleReturnToDraft,
    handleForfeitRejoin,
    handleViewCompletedMatch,
    handleDismissCompletedMatch,
    handleDismissPartyDropout,
  };
}
